import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminAuthors() {
    const { getAuthHeaders } = useAuth();
    const [authors, setAuthors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingAuthor, setEditingAuthor] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        bio: '',
        portrait_url: ''
    });

    const fetchAuthors = async () => {
        try {
            const response = await axios.get(`${API}/admin/authors`, {
                headers: getAuthHeaders()
            });
            setAuthors(response.data);
        } catch (error) {
            console.error('Failed to fetch authors:', error);
            toast.error('Nepodařilo se načíst autory');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAuthors();
    }, [getAuthHeaders]);

    const openCreateDialog = () => {
        setEditingAuthor(null);
        setFormData({ name: '', bio: '', portrait_url: '' });
        setDialogOpen(true);
    };

    const openEditDialog = (author) => {
        setEditingAuthor(author);
        setFormData({
            name: author.name,
            bio: author.bio || '',
            portrait_url: author.portrait_url || ''
        });
        setDialogOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            if (editingAuthor) {
                await axios.put(`${API}/admin/authors/${editingAuthor.id}`, formData, {
                    headers: getAuthHeaders()
                });
                toast.success('Autor byl upraven');
            } else {
                await axios.post(`${API}/admin/authors`, formData, {
                    headers: getAuthHeaders()
                });
                toast.success('Autor byl vytvořen');
            }
            setDialogOpen(false);
            fetchAuthors();
        } catch (error) {
            toast.error('Nepodařilo se uložit autora');
        }
    };

    const handleDelete = async (authorId) => {
        if (!confirm('Opravdu chcete smazat tohoto autora?')) return;
        
        try {
            await axios.delete(`${API}/admin/authors/${authorId}`, {
                headers: getAuthHeaders()
            });
            toast.success('Autor byl smazán');
            fetchAuthors();
        } catch (error) {
            toast.error('Nepodařilo se smazat autora');
        }
    };

    return (
        <div data-testid="admin-authors">
            <div className="flex items-center justify-between mb-8">
                <h1 className="font-heading text-2xl md:text-3xl font-semibold">
                    Správa autorů
                </h1>
                <Button onClick={openCreateDialog} className="listarna-btn-primary" data-testid="create-author-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Nový autor
                </Button>
            </div>

            {/* Authors Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {loading ? (
                    [1, 2, 3].map((i) => (
                        <div key={i} className="animate-pulse listarna-card rounded-lg p-6">
                            <div className="w-20 h-20 bg-secondary rounded-full mx-auto mb-4" />
                            <div className="h-5 bg-secondary rounded w-3/4 mx-auto mb-2" />
                            <div className="h-4 bg-secondary rounded w-full" />
                        </div>
                    ))
                ) : authors.length === 0 ? (
                    <div className="col-span-full text-center py-12 text-muted-foreground">
                        Žádní autoři
                    </div>
                ) : (
                    authors.map((author) => (
                        <div 
                            key={author.id} 
                            className="listarna-card rounded-lg p-6 text-center"
                            data-testid={`author-card-${author.id}`}
                        >
                            {author.portrait_url ? (
                                <img 
                                    src={author.portrait_url} 
                                    alt={author.name}
                                    className="w-20 h-20 rounded-full object-cover mx-auto mb-4"
                                />
                            ) : (
                                <div className="w-20 h-20 rounded-full bg-secondary flex items-center justify-center mx-auto mb-4">
                                    <span className="font-heading text-2xl text-muted-foreground">
                                        {author.name.charAt(0)}
                                    </span>
                                </div>
                            )}
                            <h3 className="font-heading text-lg font-medium mb-1">
                                {author.name}
                            </h3>
                            <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                                {author.bio || 'Bez popisu'}
                            </p>
                            <p className="text-xs text-muted-foreground mb-4">
                                {author.story_count} povídek
                            </p>
                            <div className="flex items-center justify-center gap-2">
                                <button
                                    onClick={() => openEditDialog(author)}
                                    className="p-2 hover:bg-secondary rounded-lg"
                                    title="Upravit"
                                    data-testid={`edit-author-${author.id}`}
                                >
                                    <Edit className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => handleDelete(author.id)}
                                    className="p-2 hover:bg-destructive/10 hover:text-destructive rounded-lg"
                                    title="Smazat"
                                    data-testid={`delete-author-${author.id}`}
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">
                            {editingAuthor ? 'Upravit autora' : 'Nový autor'}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                        <div>
                            <Label htmlFor="name">Jméno</Label>
                            <Input
                                id="name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                required
                                data-testid="author-name-input"
                            />
                        </div>

                        <div>
                            <Label htmlFor="bio">Bio</Label>
                            <Textarea
                                id="bio"
                                value={formData.bio}
                                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                                rows={3}
                                data-testid="author-bio-input"
                            />
                        </div>

                        <div>
                            <Label htmlFor="portrait_url">URL portrétu</Label>
                            <Input
                                id="portrait_url"
                                value={formData.portrait_url}
                                onChange={(e) => setFormData({ ...formData, portrait_url: e.target.value })}
                                placeholder="https://..."
                                data-testid="author-portrait-input"
                            />
                        </div>

                        <div className="flex justify-end gap-4 pt-4">
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                Zrušit
                            </Button>
                            <Button type="submit" className="listarna-btn-primary" data-testid="author-submit-btn">
                                {editingAuthor ? 'Uložit změny' : 'Vytvořit'}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
